/* eslint-disable react/prop-types */
import { arrayOf, bool, func, node, oneOfType, shape, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState, useId } from 'react';
import IconArrowHead from '../IconArrowHead/IconArrowHead';
import IconCheckmark from '../IconCheckmark/IconCheckmark';
import Chip from './Chip';
import useIsMobile from '../../hooks/useIsMobile';

import css from './DropdownMultiEnum.module.css';
import classNames from 'classnames';

const getGroupedOptions = (options, disableInternalSort = false) => {
  const groupedOptions = options?.reduce((all, opt) => {
    const groupName = opt.group || 'default';
    all[groupName] = [...(all[groupName] || []), opt];
    return all;
  }, {});

  if (!disableInternalSort) {
    Object.keys(groupedOptions).forEach(groupName => {
      groupedOptions[groupName].sort((a, b) => {
        if (a.value === 'other') return 1;
        if (b.value === 'other') return -1;
        return a.label?.localeCompare(b.label ?? '') ?? 0;
      });
    });
  }

  return Object.entries(groupedOptions);
};

const makeSubstringBold = (text, shouldBeBold) => {
  if (!shouldBeBold) return text;
  const escaped = shouldBeBold.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escaped, 'ig');
  const textArray = text.split(regex);
  const match = text.match(regex);
  return (
    <span>
      {textArray.map((item, index) => (
        <React.Fragment key={index}>
          {item}
          {index !== textArray.length - 1 && match && <b>{match[index]}</b>}
        </React.Fragment>
      ))}
    </span>
  );
};

const MOBILE_BREAKPOINT = 768;
const DRAWER_VARIANT = 'drawer';
const WHITE_VARIANT = 'white';

const DropdownMultiEnum = props => {
  const {
    options = [],
    className,
    inputIcon,
    placeholder,
    onChange,
    value = [],
    onFocus,
    onBlur,
    name,
    id,
    disableRest,
    searchable = true,
    disabled,
    customIcon = null,
    multiSelect = true,
    disableInternalSort = false,
    hideChips = true,
    variant,
    onToggle,
  } = props;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const isMobile = useIsMobile(MOBILE_BREAKPOINT);
  const isDrawerVariant = variant === DRAWER_VARIANT || isMobile;

  // unique drawer key (prefer provided id)
  const reactId = useId();
  const drawerKey = id ?? reactId;

  const dropdownRef = useRef(null);
  const optionsRefs = useRef([]);
  const listRef = useRef(null);

  const handleSearch = value => {
    setSearchString(value);
  };

  const remainingOptions = useMemo(() => {
    return options.filter(option =>
      option.searchString?.toLowerCase().includes(searchString.toLowerCase())
    );
  }, [options, searchString]);

  const remainingOptionsByGroup = useMemo(
    () =>
      getGroupedOptions(
        remainingOptions.map((option, index) => ({ ...option, index })),
        disableInternalSort
      ),
    [remainingOptions, disableInternalSort]
  );

  const dropdownClasses = classNames(className, css.dropdown, {
    [css.dropdownOpen]: dropdownOpen,
  });

  const chooseOption = useCallback(
    newValue => {
      if (multiSelect) {
        typeof newValue === 'string'
          ? onChange([...value, newValue])
          : onChange([...new Set([...value, ...newValue])]);
      } else {
        onChange(newValue);
        const option = options.find(o => o.value === newValue);
        setSearchString(option?.label ?? option?.value ?? '');
        setDropdownOpen(false);
      }
    },
    [onChange, value, multiSelect, options, isDrawerVariant, drawerKey]
  );

  const removeOption = useCallback(
    valueToRemove => {
      if (multiSelect) {
        typeof valueToRemove === 'string'
          ? onChange(value.filter(val => val !== valueToRemove))
          : onChange(value.filter(el => !valueToRemove.includes(el)));
      } else {
        onChange(undefined);
      }
    },
    [onChange, value, multiSelect]
  );

  const optionChecked = useCallback(
    option => {
      if (multiSelect) {
        return typeof option.value === 'string'
          ? value.includes(option.value)
          : option.value.every(v => value.includes(v));
      } else {
        return value === option.value;
      }
    },
    [value, multiSelect]
  );

  const optionDisabled = useCallback(option => disableRest && !optionChecked(option), [
    disableRest,
    optionChecked,
  ]);

  const handleOptionClicked = useCallback(
    option => {
      if (optionDisabled(option)) return;
      !optionChecked(option) ? chooseOption(option.value) : removeOption(option.value);
    },
    [chooseOption, optionChecked, optionDisabled, removeOption]
  );

  const getOptionByValue = value => options.find(option => option.value === value);

  const scrollIntoViewIfNeeded = target => {
    if (!listRef.current || !target) return;
    const listBounds = listRef.current.getBoundingClientRect();
    const targetBounds = target.getBoundingClientRect();

    if (targetBounds.bottom > listBounds.bottom) {
      target.scrollIntoView(false);
    } else if (targetBounds.top < listBounds.top) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  // Click outside & keyboard navigation
  useEffect(() => {
    const closeDropdownOnClickOutside = e => {
      if (dropdownOpen && !dropdownRef.current?.contains(e.target)) {
        setDropdownOpen(false);
        onBlur?.();
      }
    };

    const keyDownCallback = e => {
      if (!dropdownOpen) return;

      let newIndex;
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          newIndex = activeIndex > 0 ? activeIndex - 1 : activeIndex;
          scrollIntoViewIfNeeded(optionsRefs.current[newIndex]);
          setActiveIndex(newIndex);
          return;
        case 'ArrowDown':
          e.preventDefault();
          newIndex = activeIndex < remainingOptions.length - 1 ? activeIndex + 1 : activeIndex;
          scrollIntoViewIfNeeded(optionsRefs.current[newIndex]);
          setActiveIndex(newIndex);
          return;
        case 'Enter':
          e.preventDefault();
          if (activeIndex < 0) return;
          handleOptionClicked(remainingOptions.find((_, index) => index === activeIndex));
          return;
        default:
          return;
      }
    };

    document.addEventListener('click', closeDropdownOnClickOutside);
    document.addEventListener('keydown', keyDownCallback);
    return () => {
      document.removeEventListener('click', closeDropdownOnClickOutside);
      document.removeEventListener('keydown', keyDownCallback);
    };
  }, [
    activeIndex,
    dropdownOpen,
    handleOptionClicked,
    onBlur,
    remainingOptions,
    isDrawerVariant,
    drawerKey,
  ]);

  useEffect(() => {
    if (!multiSelect) {
      const option = getOptionByValue(value);
      setSearchString(option?.label ?? option?.value ?? '');
    }
  }, [value, multiSelect]);

  // Display text inside the input/button
  const displayText = useMemo(() => {
    if (multiSelect) return `${placeholder}${value?.length > 0 ? ` (${value.length})` : ''}`;
    return searchString || placeholder || '';
  }, [multiSelect, placeholder, value, searchString]);

  // Helper for rendering the "input" area. For drawer variant this will be a button
  const renderInputOrButton = () => {
    const commonClasses = classNames(css.input, {
      [css.withIcon]: !!inputIcon,
      [css.inputMobile]: isMobile && !(variant === WHITE_VARIANT),
      [css.inputDrawer]: variant === DRAWER_VARIANT,
      [css.whiteVariant]: variant === WHITE_VARIANT,
    });

    if (isDrawerVariant) {
      // Render a button that looks and behaves like the input (visually identical)
      return (
        <>
          {/* hidden input to preserve form semantics if name/id used */}
          {name && (
            <input
              type="hidden"
              name={name}
              value={Array.isArray(value) ? value.join(',') : value ?? ''}
            />
          )}

          <button
            id={id}
            type="button"
            className={commonClasses}
            onClick={e => {
              // toggle
              setDropdownOpen(prev => {
                const next = !prev;

                return next;
              });
              onFocus?.(e);
              onToggle?.();
            }}
            onFocus={e => onFocus?.(e)}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
          >
            {inputIcon && <span className={css.icon}>{inputIcon}</span>}
            <span className={css.inputValue} aria-hidden>
              {displayText}
            </span>
          </button>
        </>
      );
    }

    // Regular input for non-drawer variants
    return (
      <input
        id={id}
        name={name}
        className={commonClasses}
        value={searchString}
        onChange={e => searchable && handleSearch(e.target.value)}
        onFocus={e => {
          setDropdownOpen(true);
          onFocus?.(e);
          onToggle?.();
        }}
        placeholder={`${placeholder}${
          multiSelect && value?.length > 0 ? ` (${value.length})` : ''
        }`}
        readOnly={!searchable || isDrawerVariant}
        disabled={disabled}
      />
    );
  };

  return (
    <div className={dropdownClasses} ref={dropdownRef} data-drawer={drawerKey}>
      <div className={css.dropdownTrigger}>
        <div className={classNames(css.inputWrapper, {
          [css.inputWrapperOpen]: isMobile && dropdownOpen,
        })}>
          {renderInputOrButton()}

          <button
            type="button"
            className={css.openBtn}
            onClick={() => {
              setDropdownOpen(prev => {
                const next = !prev;

                return next;
              });
              if (dropdownOpen) onBlur?.();
              onToggle?.();
            }}
            aria-label={dropdownOpen ? 'Close options' : 'Open options'}
          >
            {!isMobile &&
              (customIcon ??
                (dropdownOpen ? (
                  <IconArrowHead direction="up" />
                ) : (
                  <IconArrowHead direction="down" />
                )))}

            {isMobile &&
              (customIcon ??
                (dropdownOpen ? (
                  <IconArrowHead direction="left" />
                ) : (
                  <IconArrowHead direction="right" />
                )))}
          </button>
        </div>
      </div>
      {isDrawerVariant && <div className={css.divider} />}
      {dropdownOpen && (
        <ul
          className={classNames(css.optionsList, {
            [css.drawerVariantList]: variant === DRAWER_VARIANT,
            [css.whiteVariantList]: variant === WHITE_VARIANT,
          })}
          ref={listRef}
          role="listbox"
        >
          {remainingOptionsByGroup.map(([groupName, groupOptions]) => (
            <React.Fragment key={groupName}>
              {groupName !== 'default' && <li className={css.group} role="separator"></li>}
              {groupOptions.map(option => (
                <li
                  key={option.value}
                  ref={el => (optionsRefs.current[option.index] = el)}
                  onClick={() => {
                    handleOptionClicked(option);
                    onBlur?.();
                  }}
                  className={css.option}
                  role="option"
                  aria-selected={option.index === activeIndex}
                  aria-disabled={optionDisabled(option)}
                  onMouseMove={() => setActiveIndex(option.index)}
                >
                  {optionChecked(option) && (
                    <IconCheckmark size="small" className={css.checkIcon} />
                  )}
                  {option.icon && <span className={css.optionIcon}>{option.icon}</span>}
                  {makeSubstringBold(option.label, searchString)}
                </li>
              ))}
            </React.Fragment>
          ))}
        </ul>
      )}

      {!hideChips && multiSelect && value.length > 0 && (
        <div className={css.chipsContainer}>
          {value.map(val => (
            <Chip
              key={val}
              option={getOptionByValue(val)}
              val={val}
              removeOption={removeOption}
              onBlur={onBlur}
            />
          ))}
        </div>
      )}
    </div>
  );
};


DropdownMultiEnum.propTypes = {
  id: string,
  className: string,
  dropdownInputClassName: string,
  inputIcon: node,
  placeholder: string,
  onChange: func,
  onBlur: func,
  onFocus: func,
  disableRest: bool,
  disableInternalSort: bool,
  customIcon: node,
  options: arrayOf(
    shape({
      label: oneOfType([string, node]).isRequired,
      searchString: string,
      value: oneOfType([string, arrayOf(string)]).isRequired,
      icon: node,
      group: string,
    })
  ),
  value: oneOfType([arrayOf(string), string]),
  multiSelect: bool,
  variant: string,
};

export default DropdownMultiEnum;
